import React, { useState } from 'react'
import Highlighter from 'react-highlight-words'
import { defineMessages, FormattedMessage } from 'react-intl'
import { View } from 'react-native'

import { every, isEmpty, isEqual } from 'lodash-es'
import PropTypes from 'prop-types'
import styled from 'styled-components/native'

import { BodyText } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/bodyText/BodyText'
import { TypeAheadFieldAdapter } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/formElements/typeAhead/TypeAheadFieldAdapter'
import { PrimaryButton } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/primaryButton/PrimaryButton'
import { TextButton } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/textButton/TextButton'
import { Title } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/title/Title'
import { TitleSize } from '@lyrahealth-inc/ui-core-crossplatform/src/styles/typeStyles'
import { ThemeType } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/themes/ThemeProvider'
import { tID } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/utils'

import styles from './icd10s.module.scss'
import TabFilters from '../tabFilters/TabFilters'

const CtaOuterContainer = styled.View({
  flexDirection: 'row',
  justifyContent: 'end',
})

const CtaInnerContainer = styled.View<{ theme: ThemeType }>(({ theme: { spacing } }) => ({
  paddingTop: spacing['32px'],
  flexDirection: 'row',
  gap: spacing['40px'],
  alignItems: 'center',
}))

const ScrollContainer = styled.ScrollView({
  maxHeight: '347px',
})

const SubTitleContainer = styled.View<{ theme: ThemeType }>(({ theme: { spacing } }) => ({
  marginBottom: spacing['24px'],
}))

const messages = defineMessages({
  addICD10DiagnosisCodeSingle: {
    defaultMessage: 'Add ICD-10 Diagnosis Code',
    description: 'Title for adding a single ICD-10 diagnosis code',
  },
  addICD10DiagnosisCodeMultiple: {
    defaultMessage: 'Add ICD-10 Diagnosis Codes',
    description: 'Title for adding multiple ICD-10 diagnosis codes',
  },
  addAdditionalDiagnosisCodes: {
    defaultMessage: 'Add additional diagnosis codes as appropriate.',
    description: 'Subtitle for adding multiple diagnosis codes',
  },
  addOnePrimaryDiagnosis: {
    defaultMessage: 'Add one primary diagnosis.',
    description: 'Subtitle for adding a single primary diagnosis',
  },
  cancel: {
    defaultMessage: 'Cancel',
    description: 'Cancel button text',
  },
  add: {
    defaultMessage: 'Add',
    description: 'Add button text',
  },
})

const ICD10sSelection = ({
  initialCodes = [],
  multiSelect,
  availableCodes = {},
  closeModal,
  submitFunction,
  duplicateCodes = [],
}: any) => {
  const { common: commonCodes, all: allCodes } = availableCodes
  const [activeTab, setActiveTab] = useState('Common')
  const [selectedCodes, setSelectedCodes] = useState(initialCodes)

  const handleSelectedCodesChange = (codes: any) => {
    setSelectedCodes(codes)
  }

  const onSubmit = (codes: any) => {
    setSelectedCodes([])
    setActiveTab('Common')
    submitFunction(codes)
  }

  const renderMenuItemChildren = (option: any, fieldProps: any) => (
    <div className={styles['option-container']}>
      <div className={styles['code-capsule']}>
        <Highlighter
          highlightClassName='mark'
          searchWords={fieldProps.text.split(' ')}
          autoEscape
          textToHighlight={option.code}
        />
      </div>
      <div className={styles.description}>
        <Highlighter
          highlightClassName='mark'
          searchWords={fieldProps.text.split(' ')}
          autoEscape
          textToHighlight={option.desc}
        />
      </div>
    </div>
  )

  const toggleCommonCode = (option: any, isSelected: any) => {
    setSelectedCodes(
      isSelected ? selectedCodes.filter((obj: any) => obj.code !== option.code) : [...selectedCodes, option],
    )
  }

  const isDuplicateCode = (duplicateCodes: { code: string }[], optionCode: string): boolean => {
    return duplicateCodes.some((duplicateCode: { code: string }) => duplicateCode.code === optionCode)
  }

  const renderCommonCode = (option: any) => {
    const isSelected = selectedCodes.some((o: any) => isEqual(o, option))
    const isDisabled =
      (selectedCodes.length > 0 && !multiSelect && !isSelected) || isDuplicateCode(duplicateCodes, option.code)
    return (
      <li key={option.code}>
        <button
          type='button'
          className={isDisabled ? styles.disabled : undefined}
          disabled={isDisabled}
          aria-pressed={isSelected}
          onClick={() => toggleCommonCode(option, isSelected)}
        >
          <div
            className={`${styles['option-container']} ${isSelected ? styles.selected : ''} ${
              isDisabled ? styles.disabled : ''
            }`}
          >
            <div className={styles['code-capsule']}>{option.code}</div>
            <div className={styles.description}>{option.desc}</div>
          </div>
        </button>
      </li>
    )
  }
  return (
    <div className={styles['icd10s-selection-container']}>
      <View>
        <Title
          text={
            <FormattedMessage
              {...(multiSelect ? messages.addICD10DiagnosisCodeMultiple : messages.addICD10DiagnosisCodeSingle)}
            />
          }
          size={TitleSize.MEDIUM}
        />
      </View>
      <SubTitleContainer>
        <BodyText
          text={
            <FormattedMessage
              {...(multiSelect ? messages.addAdditionalDiagnosisCodes : messages.addOnePrimaryDiagnosis)}
            />
          }
        />
      </SubTitleContainer>
      {/* @ts-expect-error TS(2322): Type 'Dispatch<SetStateAction<string>>' is not ass... Remove this comment to see the full error message */}
      <TabFilters tabList={['Common', 'Search']} className={styles.tabs} handleClick={setActiveTab} />
      {activeTab === 'Common' ? (
        <div className='row'>
          <div className={`col-xs-12 ${styles['common-container']}`}>
            <ScrollContainer>
              <ul>{commonCodes.map(renderCommonCode)}</ul>
            </ScrollContainer>
          </div>
        </div>
      ) : (
        <div className='row'>
          <div className={`col-xs-12 ${styles['field-container']}`}>
            <TypeAheadFieldAdapter
              input={{
                name: 'codes',
                value: selectedCodes,
              }}
              options={allCodes}
              alwaysOpen
              emptyLabel='No results found'
              filterBy={(option: any, fieldProps: any) => {
                // Don't show selected options in the menu for the multi-select case.
                if (fieldProps.multiple && fieldProps.selected.some((o: any) => isEqual(o, option))) {
                  return false
                }
                // Don't show options if they were already selected as a Primary Diagnosis
                // and we are in Secondary Diagnosis, or vice versa
                if (isDuplicateCode(duplicateCodes, option.code)) {
                  return false
                }
                const searchArray = fieldProps.text.split(' ')
                return every(
                  searchArray,
                  (s) => fieldProps.labelKey(option).toLowerCase().indexOf(s.toLowerCase()) !== -1,
                )
              }}
              placeholder='Search all codes or descriptions'
              multiSelect={multiSelect}
              onChange={handleSelectedCodesChange}
              labelKey={(option: any) => `${option.code} ${option.desc}`}
              renderMenuItemChildren={renderMenuItemChildren}
            />
          </div>
        </div>
      )}
      <CtaOuterContainer>
        <CtaInnerContainer>
          <View>
            <TextButton
              text={<FormattedMessage {...messages.cancel} />}
              onPress={closeModal}
              onPressIn={closeModal}
              testID={tID('ICD10sSelection-cancelSelection')}
            />
          </View>
          <PrimaryButton
            testID={tID('ICD10sSelection-addSelection')}
            disabled={isEmpty(selectedCodes)}
            onPressIn={() => onSubmit(selectedCodes)}
            onPress={() => onSubmit(selectedCodes)}
            text={<FormattedMessage {...messages.add} />}
          />
        </CtaInnerContainer>
      </CtaOuterContainer>
    </div>
  )
}

ICD10sSelection.propTypes = {
  initialCodes: PropTypes.array,
  submitFunction: PropTypes.func,
  closeModal: PropTypes.func,
  availableCodes: PropTypes.object,
  multiSelect: PropTypes.bool,
  duplicateCodes: PropTypes.array,
}

export default ICD10sSelection
